import "../index.css";
import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  BalanceShape,
  TriangleShape,
  ArrowShape,
  VennShape,
  EqualsShape,
  NestedSquareShape,
  GoalsShape,
} from "../components/PillarIcons";
import Footer from "../components/Footer";
import { PageNav } from "../components/PageNav";
import { Selfie2 } from "../components/Logo";

const pillars = [
  { icon: BalanceShape, label: "Balance" },
  { icon: ArrowShape, label: "Strategic" },
  { icon: TriangleShape, label: "Transformation" },
  { icon: VennShape, label: "Responsible" },
  { icon: EqualsShape, label: "Organized" },
  { icon: NestedSquareShape, label: "Natural Fit" },
  { icon: GoalsShape, label: "Goals" },
];

const services = [
  {
    title: "1:1 Executive Coaching",
    text: "Acting as a sounding board for executive leaders to work ideas, perspectives, and approaches to leading their organizations and teams.",
  },
  {
    title: "Leadership Development",
    text: "Supporting in-role, new-to-role, and prospective leaders in growing their leadership skills and capabilities.",
  },
  {
    title: "Team Dynamics",
    text: "Working with leaders and teams on how teams operate, the dynamics of leadership and followership, and team health.",
  },
  {
    title: "Organizational Health",
    text: "Exploring “how things are done here” to align leadership priorities with team execution and the culture that underpins it all.",
  },
];

const gains = [
  "A grounded thought partner bringing nearly three decades of leadership development experience",
  "Neuroscience informed perspectives",
  "Clearer thinking and mature leadership practices",
  "More confident, intentional decision-making aligned to clear measures",
  "An authentic, sustainable, and balanced leadership identity",
];

const testimonials = [
  {
    name: "VP of Operations",
    role: "Regional Healthcare Network",
    text: "Brian helped me separate what was urgent from what actually mattered. Six months in, I was leading with a plan instead of reacting to whatever was loudest that day.",
  },
  {
    name: "Founder & CEO",
    role: "Professional Services Firm",
    text: "The coaching engagement gave me a framework I still use every week. It's rare to find someone who blends real corporate experience with genuine curiosity about how people work.",
  },
  {
    name: "Emerging Leader",
    role: "Fortune 200 Utility",
    text: "I came in questioning whether I belonged in the role. I left with a clear, authentic leadership identity and a concrete plan for the next stage of my career.",
  },
];

export default function Home() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div>
      <PageNav />

      <div className="bsp-brushed-metal">
        <div className="bsp-hero">
          {/* <div className="bsp-eyebrow font_family_base">
            BStrong Performance &middot; Est. 2005
          </div> */}
          <h1 className="bsp-h1 font_family_base">
            Lead With Clarity, Balance, and Purpose
          </h1>
          <p
            className="bsp-lead font_family_base"
            style={{ maxWidth: 700, margin: "0 auto" }}
          >
            Coaching excellence combined with data-driven insight to help
            leaders grow with clarity and confidence.
          </p>
          <div className="bsp-hero-actions">
            <Link
              to="/services"
              className="bsp-btn bsp-btn-primary font_family_base"
            >
              What We Do
            </Link>
            <Link
              to="/contact"
              className="bsp-btn bsp-btn-outline font_family_base"
            >
              Schedule a Conversation
            </Link>
          </div>
        </div>

        <div
          className="bsp-container"
          style={{ paddingBottom: 60, paddingLeft: 20, paddingRight: 20 }}
        >
          <div className="bsp-grid bsp-grid-7">
            {pillars.map(({ icon: Icon, label }) => (
              <div key={label} className="bsp-pillar">
                <div className="bsp-pillar-icon">
                  <Icon />
                </div>
                <div className="bsp-pillar-label font_family_base">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bsp-section bsp-container font_family_base">
        <div
          style={{
            display: "flex",
            gap: 48,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <div style={{ width: 260, flexShrink: 0, margin: "0 auto" }}>
            <Selfie2 />
          </div>
          <div style={{ flex: 1, minWidth: 280 }}>
            <div className="bsp-eyebrow">Meet Your Coach</div>
            <h2 className="bsp-h2">Brian A. Armstrong, Ph.D., CPCC</h2>
            <p className="bsp-body" style={{ marginBottom: 16 }}>
              Founder &amp; Principal Consultant of BStrong Performance, Brian
              brings nearly three decades of leadership development and coaching
              experience &mdash; credentialed in MBTI, Hogan, Insights, EQ-i
              2.0, Risk Type Compass, and more &mdash; across corporate,
              consulting, and higher-education settings.
            </p>
            <p className="bsp-body" style={{ marginBottom: 24 }}>
              If you're carrying the weight of leadership, and you want to be
              more intentional and purposeful, you're in the right place.
            </p>
            <Link
              to="/about"
              className="bsp-btn bsp-btn-outline font_family_base"
            >
              About BStrong
            </Link>
          </div>
        </div>
      </div>

      <div className="bsp-section bsp-container font_family_base">
        <div className="bsp-eyebrow bsp-center">Available Services</div>
        <h2 className="bsp-h2 bsp-center" style={{ marginBottom: 36 }}>
          How We Work Together
        </h2>
        <div className="bsp-grid bsp-grid-4">
          {services.map((s) => (
            <div key={s.title} className="bsp-card">
              <h3 className="bsp-h3">{s.title}</h3>
              <p className="bsp-body">{s.text}</p>
            </div>
          ))}
        </div>
        <div className="bsp-center" style={{ marginTop: 32 }}>
          <Link
            to="/services"
            className="bsp-btn bsp-btn-primary font_family_base"
          >
            Explore Services
          </Link>
        </div>
      </div>

      <div className="bsp-section-narrow bsp-container-narrow font_family_base">
        <div className="bsp-card-alt">
          <h2 className="bsp-h2">What You Gain</h2>
          <ul className="bsp-list">
            {gains.map((g) => (
              <li key={g}>{g}</li>
            ))}
          </ul>
        </div>
      </div>

      <div className="bsp-section bsp-container font_family_base">
        <h2 className="bsp-h2 bsp-center" style={{ marginBottom: 36 }}>
          What Leaders Say
        </h2>
        <div className="bsp-grid bsp-grid-3">
          {testimonials.map((t) => (
            <div key={t.name} className="bsp-testimonial">
              <div className="bsp-stars">
                &#9733;&#9733;&#9733;&#9733;&#9733;
              </div>
              <p className="bsp-body" style={{ fontStyle: "italic", flex: 1 }}>
                &ldquo;{t.text}&rdquo;
              </p>
              <div>
                <div style={{ fontWeight: 700, color: "#ffffff" }}>
                  {t.name}
                </div>
                <div style={{ fontSize: "0.85rem", color: "#9aa1ab" }}>
                  {t.role}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bsp-section bsp-cta-band bsp-center font_family_base">
        <h2 className="bsp-h2">Ready to Lead With More Clarity?</h2>
        <p className="bsp-lead" style={{ marginBottom: 8 }}>
          Let's talk about where you are and where you want to be.
        </p>
        <div className="bsp-hero-actions">
          <Link to="/contact" className="bsp-btn bsp-btn-primary">
            Get In Touch
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
}
